import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
    FaBell, FaCog, FaGift, FaHeadset, FaHeart, FaHome, FaShoppingCart,
    FaStore, FaUser, FaClipboardList, FaExternalLinkAlt
} from 'react-icons/fa'
import { loadQuickMenu } from '../utilies/quickMenuCache'

const ICONS = {
    cart: FaShoppingCart,
    orders: FaClipboardList,
    support: FaHeadset,
    heart: FaHeart,
    user: FaUser,
    shop: FaStore,
    home: FaHome,
    settings: FaCog,
    bell: FaBell,
    gift: FaGift
}

const HIDDEN_PATHS = ['/login', '/register', '/forgot-password', '/oauth-success', '/shipping', '/payment']

const shouldHideOnPath = pathname =>
    HIDDEN_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`)) ||
    pathname.startsWith('/admin/') ||
    pathname.startsWith('/seller/')

const isActivePath = (pathname, target) => {
    if (target === '/') return pathname === '/'
    return pathname === target || pathname.startsWith(`${target}/`)
}

const FloatingQuickMenu = () => {
    const location = useLocation()
    const { t } = useTranslation()
    const { userInfo, authChecked } = useSelector(state => state.auth)
    const [menu, setMenu] = useState(null)

    useEffect(() => {
        let mounted = true
        // Deduped, session-cached fetch shared with MobileFloatingQuickMenu.
        // Resolves to a safe { items, settings } payload; existing guards below
        // (isEnabled / items length / path) decide visibility exactly as before.
        loadQuickMenu().then((data) => {
            if (mounted) setMenu({ items: data.items, settings: data.settings })
        })
        return () => { mounted = false }
    }, [])

    const items = useMemo(() => {
        if (!menu) return []
        const role = userInfo ? 'customer' : 'guest'
        return menu.items.filter(item => item.roleVisibility === 'all' || item.roleVisibility === role)
    }, [menu, userInfo])

    if (!authChecked || !menu || !menu.settings?.isEnabled || !items.length || shouldHideOnPath(location.pathname)) return null

    const settings = menu.settings
    const onLeft = settings.position === 'left'
    const compact = settings.style === 'compact'
    const light = settings.theme === 'light'
    const desktopSide = onLeft ? 'left-4 xl:left-6' : 'right-4 xl:right-6'
    const mobileSide = onLeft ? 'md-lg:left-3' : 'md-lg:right-3'
    // Mobile (≤991px) is owned by MobileFloatingQuickMenu (radial fan). This
    // rail stays desktop-only to avoid duplicate menus / bottom-nav overlap.
    const mobileVisibility = 'md-lg:hidden'
    const shellTheme = light
        ? 'border-slate-200/90 bg-white/90 text-slate-600 shadow-[0_18px_50px_rgba(15,23,42,0.16)]'
        : 'border-white/10 bg-slate-950/85 text-slate-300 shadow-[0_18px_55px_rgba(15,23,42,0.42)]'

    return (
        <nav
            aria-label={t('quickMenu.aria')}
            className={`fixed top-1/2 z-50 flex -translate-y-1/2 flex-col items-center rounded-full border backdrop-blur-xl ${desktopSide} ${mobileSide} ${mobileVisibility} ${shellTheme} ${compact ? 'gap-0.5 p-1.5' : 'gap-1 p-2'} md-lg:top-auto md-lg:bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] md-lg:max-w-[calc(100vw-1.5rem)] md-lg:translate-y-0 md-lg:flex-row md-lg:overflow-x-auto md-lg:p-1.5`}
        >
            {items.map((item, index) => {
                const Icon = ICONS[item.icon] || FaHome
                const active = item.type === 'internal' && isActivePath(location.pathname, item.url)
                const commonProps = {
                    'aria-label': item.label || item.title,
                    title: !settings.showLabelsOnHover ? (item.label || item.title) : undefined,
                    className: `group relative flex shrink-0 items-center justify-center rounded-full outline-none transition duration-200 hover:scale-110 focus-visible:ring-2 focus-visible:ring-violet-400 ${
                        compact ? 'h-8 w-8' : 'h-10 w-10 md-lg:h-9 md-lg:w-9'
                    } ${active ? 'bg-violet-500/20 shadow-[0_0_18px_rgba(139,92,246,.5)]' : light ? 'hover:bg-slate-100' : 'hover:bg-white/10'}`,
                    style: { color: item.color || '#8b5cf6' }
                }
                const content = (
                    <>
                        <Icon size={compact ? 14 : 17} aria-hidden='true' />
                        {item.type === 'external' && <FaExternalLinkAlt className='absolute right-0.5 top-0.5 text-[7px] opacity-60' aria-hidden='true' />}
                        {item.badgeKey && (
                            <span className='sr-only'>Badge dəstəyi hazırlanıb; dinamik say aktiv deyil.</span>
                        )}
                        {settings.showLabelsOnHover && (
                            <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-950 px-2.5 py-1.5 text-[11px] font-semibold text-white opacity-0 shadow-xl transition group-hover:opacity-100 group-focus-visible:opacity-100 md-lg:hidden ${onLeft ? 'left-full ml-3' : 'right-full mr-3'}`}>
                                {item.label || item.title}
                            </span>
                        )}
                    </>
                )

                return (
                    <div key={item._id || `${item.url}-${index}`} className='contents'>
                        {item.type === 'external' ? (
                            <a
                                href={item.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                {...commonProps}
                            >
                                {content}
                            </a>
                        ) : (
                            <Link
                                to={item.url}
                                target={item.openInNewTab ? '_blank' : undefined}
                                rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                                {...commonProps}
                            >
                                {content}
                            </Link>
                        )}
                        {index < items.length - 1 && (
                            <span className={`h-px w-5 ${light ? 'bg-slate-200' : 'bg-white/10'} md-lg:h-5 md-lg:w-px`} aria-hidden='true' />
                        )}
                    </div>
                )
            })}
        </nav>
    )
}

export default FloatingQuickMenu
